import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { useRef } from "react";
import { requests } from "../../services/http-common";
import classes from "./import-export-data.module.scss";
import { classNames as cx } from "primereact/utils";
import { useToast } from "../../Providers/ToastProvider";
import { useUser } from "../../keycloak";
import { UserRoles } from "../../constants/userRoles";
import { exportFileNameFromPath } from "./import-export-data.utils";

type ImportExportDataProps = {
  exportUrl: string;
  importUrl: string;
  refetchQury: () => void;
  // The backend gates each export on its own resource permission; default to the
  // employee permission so existing EmployeeList usage is unchanged.
  viewRole?: UserRoles;
};

export const ImportExportData = ({
  exportUrl,
  importUrl,
  refetchQury,
  viewRole = UserRoles.USER_VIEW,
}: ImportExportDataProps) => {
  const { show } = useToast();
  const menu = useRef<any>(null);
  const importDataInputRef = useRef<any>(null);

  const user: any = useUser();

  const exportLeads = useQuery({
    queryKey: [`export-data-${exportUrl}`],
    queryFn: () => {
      return requests.post(exportUrl, {});
    },
    onSuccess: (resp) => {
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${resp}`;
      link.setAttribute("download", exportFileNameFromPath(resp));

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.parentNode && link.parentNode.removeChild(link);
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
    retry: false,
    enabled: false,
  });

  const importData = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return requests.post(importUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        transformRequest: (formData: any) => formData,
      });
    },
    onSuccess: () => {
      refetchQury();
      show({ detail: "Item added successfully !" });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const items = [
    ...(user?.roles.dcs_client.includes(viewRole)
      ? [
          {
            label: "Export Data CSV",
            command: () => {
              exportLeads.refetch();
            },
          },
        ]
      : []),
    ...(user?.roles.dcs_client.includes(UserRoles.USER_CREATE)
      ? [
          {
            label: "Import Data CSV",
            command: () => {
              importDataInputRef.current.click();
            },
          },
        ]
      : []),
  ];

  return (
    <div className={classes.content}>
      <Button
        icon="pi pi-upload"
        className={cx("p-button-text p-button-secondary", classes.exportButton)}
        onClick={(event) => menu.current && menu.current.toggle(event)}
      />
      {items.length ? (
        <Menu model={items} popup ref={menu} id="import-export-menu" />
      ) : null}
      <input
        type="file"
        ref={importDataInputRef}
        className={classes.importDataInputRef}
        accept=".csv"
        onChange={(e) => {
          if (e.target.files) importData.mutate(e.target.files[0]);
        }}
      />
    </div>
  );
};
